import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Typography,
} from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import { FC } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { InputField } from '../../../../components/input-field';
import {
  JsonField,
  isValidJson,
  parseJsonOrThrow,
  stringifyJsonForEditor,
} from '../../../../components/json-field';
import { SKU } from '../../../../types/sku';
import { useAuth } from '../../../../hooks/use-auth';
import { useAxios } from '../../../../hooks/use-axios';
import { ResponseData } from '../../../../types/axios';
import { AssetFieldInput } from '../../../../components/asset-field-input';
import {
  getAssetConfig,
  skuFields,
  getTitleField,
  titleFieldLabel,
} from '../../../../config/asset-config';

interface SKUDuplicateDialogProps {
  open: boolean;
  onClose: () => void;
  sku: SKU;
}

export const SKUEditDetailsDialog: FC<SKUDuplicateDialogProps> = ({
  open,
  onClose,
  sku,
  ...other
}) => {
  const queryClient = useQueryClient();
  const { axios } = useAxios();
  const { tenant } = useAuth();

  // Tenant-owned schema: edit the SKU's field values (under `details`) as structured
  // inputs; the rest of publicMetadata stays in the free-form JSON editor.
  const assetConfig = getAssetConfig(tenant);
  const assetFields = skuFields(assetConfig);
  const extraMetadata = Object.fromEntries(
    Object.entries(sku.publicMetadata ?? {}).filter(([key]) => key !== 'details'),
  );

  const updateMutation = useMutation(async (updates: Partial<SKU>) => {
    if (!tenant) {
      throw new Error('Tenant is missing');
    }

    const url = `/skus/${sku.id}`;

    const data = {
      tenantId: tenant?.id,
      ...updates,
    };

    return axios.put<ResponseData<SKU>>(url, data);
  });

  const initialValues = {
    title: sku.title || '',
    // Structured tenant-schema field values (from publicMetadata.details).
    fieldValues: Object.fromEntries(
      assetFields.map((f) => [f.id, (sku.publicMetadata?.details?.[f.id] as string) ?? '']),
    ) as Record<string, string>,
    // Everything else stays free-form JSON.
    publicMetadata: stringifyJsonForEditor(extraMetadata),
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      submit: null,
    },
    validationSchema: Yup.object().shape({
      title: Yup.string().required('Title is required'),
      publicMetadata: Yup.string().test('is-json', 'Invalid JSON', isValidJson),
      fieldValues: Yup.object().shape(
        Object.fromEntries(
          assetFields.map((f) => {
            let validator = Yup.string();
            if (f.type === 'number') {
              validator = validator.matches(/^-?\d*\.?\d+$/, {
                message: `${f.label} must be a number`,
                excludeEmptyString: true,
              });
            }
            validator = f.required
              ? validator.required(`${f.label} is required`)
              : validator.optional();
            return [f.id, validator];
          }),
        ),
      ),
    }),
    onSubmit: async (values, helpers) => {
      const { publicMetadata, title, fieldValues } = values;

      // Free-form JSON (non-`details` keys) + the structured field values under `details`.
      const merged = { ...(parseJsonOrThrow(publicMetadata) as Record<string, any>) };
      const details: Record<string, string> = {};
      assetFields.forEach((field) => {
        const raw = fieldValues?.[field.id];
        if (raw !== '' && raw !== undefined && raw !== null) {
          details[field.id] = String(raw);
        }
      });
      // Mirror the title field's value into details too (parity with SKU create), so
      // the isTitle field (e.g. Player) survives an edit and stays copyable to items.
      const titleField = getTitleField(assetConfig);
      if (titleField && title) {
        details[titleField.id] = title;
      }
      if (Object.keys(details).length > 0) {
        merged.details = details;
      } else {
        delete merged.details;
      }

      const newSKU: Partial<SKU> = {
        title,
        publicMetadata: merged as SKU['publicMetadata'],
      };

      updateMutation.mutate(newSKU, {
        onSuccess: () => {
          toast.success(`SKU's properties were updated`);
          queryClient.invalidateQueries('sku');
          onClose();
          helpers.setStatus({ success: true });
        },
        onError: (err: any) => {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err?.response?.data?.error?.message || err?.message });
        },
        onSettled: () => {
          helpers.setSubmitting(false);
        },
      });
    },
  });

  return (
    <Dialog
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          width: '100%',
        },
      }}
      TransitionProps={{
        onExited: () => formik.resetForm(),
      }}
      {...other}
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>Edit SKU Properties</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridAutoFlow: 'row',
            }}
          >
            {/* Title */}
            <Box sx={{ mb: 3 }}>
              <InputField
                error={Boolean(formik.touched.title && formik.errors.title)}
                fullWidth
                helperText={formik.touched.title && formik.errors.title}
                label={titleFieldLabel(assetConfig)}
                name="title"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.title}
                required
              />
              <Typography color="textSecondary">
                It can be edited afterwards until the item of this SKU is protected.
              </Typography>
            </Box>

            {/* Tenant-schema field values */}
            {assetFields.map((field) => (
              <AssetFieldInput
                key={field.id}
                field={field}
                name={`fieldValues.${field.id}`}
                value={formik.values.fieldValues?.[field.id]}
                error={Boolean(
                  (formik.touched.fieldValues as any)?.[field.id] &&
                    (formik.errors.fieldValues as any)?.[field.id],
                )}
                helperText={
                  (formik.touched.fieldValues as any)?.[field.id] &&
                  (formik.errors.fieldValues as any)?.[field.id]
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            ))}

            {/* Other metadata (free-form JSON) */}
            <JsonField
              label="Other metadata"
              name="publicMetadata"
              value={formik.values.publicMetadata}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={Boolean(formik.touched.publicMetadata && formik.errors.publicMetadata)}
              helperText={formik.touched.publicMetadata && formik.errors.publicMetadata}
              placeholder="{}"
            />
          </Box>
          {formik.errors.submit && (
            <FormHelperText error sx={{ mb: 2 }}>
              {formik.errors.submit}
            </FormHelperText>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={onClose} variant="text">
            Cancel
          </Button>
          <LoadingButton
            loading={formik.isSubmitting}
            color="primary"
            type="submit"
            size="large"
            variant="contained"
          >
            Save
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

SKUEditDetailsDialog.defaultProps = {
  open: false,
};

SKUEditDetailsDialog.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  sku: PropTypes.any,
};
